import React, {
  Children,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import type {
  OptionProps,
  MenuListProps,
  GroupBase,
  SingleValue,
} from 'react-select';
import Select, { components, createFilter } from 'react-select';
import { useTheme } from 'styled-components';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { LocationSelectValue } from './LocationSelect';

export type SingleAutoCompleteOption<ValueType = number> = {
  value: ValueType | null;
  label: string;
};

export type GroupedAutoCompleteOption<ValueType = number> = {
  label: string;
  options: SingleAutoCompleteOption<ValueType>[];
};

export type AutoCompleteOption<ValueType = number> =
  | SingleAutoCompleteOption<ValueType>
  | GroupedAutoCompleteOption<ValueType>;

type AutoCompleteSelectProps<ValueType> = {
  onChange?: (newValue: ValueType | null) => void;
  value?: ValueType | null;
  isDisabled?: boolean;
  isLoading?: boolean;
  options: AutoCompleteOption<ValueType>[];
  'aria-label'?: string;
  required?: boolean;
  name?: string;
  isClearable?: boolean;
  placeholder?: string;
  maxHeight?: number;
};

interface SelectOptionType {
  label?: string;
  value?: LocationSelectValue;
}

const OPTION_HEIGHT = 38;
const SHOW_ROWS = 8;

function createCustomMenuList(isGrouped: boolean, maxHeight?: number) {
  return function CustomMenuList(
    props: MenuListProps<
      SelectOptionType,
      boolean,
      GroupBase<SelectOptionType>
    >,
  ) {
    const { children, selectProps, getValue } = props;
    const parentRef = useRef<HTMLDivElement | null>(null);
    const parentOffsetRef = React.useRef(0);

    useLayoutEffect(() => {
      parentOffsetRef.current = parentRef.current?.offsetTop ?? 0;
    }, []);

    const count = Children.count(children);
    const rowVirtualizer = useVirtualizer({
      count,
      getScrollElement: useCallback(() => parentRef.current, []),
      estimateSize: useCallback(() => OPTION_HEIGHT, []),
      scrollMargin: parentOffsetRef.current,
      overscan: 5,
    });

    const menuIsOpen = selectProps?.menuIsOpen;
    const [value] = getValue();
    const selectedItemIndex =
      Array.isArray(children) &&
      children.findIndex((i) => i.props.value === value?.value);

    useEffect(() => {
      if (menuIsOpen && selectedItemIndex) {
        rowVirtualizer.scrollToIndex(selectedItemIndex, { align: 'center' });
      }
    }, [menuIsOpen, rowVirtualizer, selectedItemIndex]);

    return Array.isArray(children) ? (
      <div
        style={{
          /*
           * Children.count() doesn't give us proper item count when using grouped options
           * Note: Don't use options from props to calculate this as that leads to infinite looping
           */
          height:
            OPTION_HEIGHT *
            (isGrouped ? SHOW_ROWS : Math.min(count, SHOW_ROWS)),
          overflowY: 'auto',
          maxHeight: maxHeight ? `${maxHeight}px` : 'none',
        }}
        ref={parentRef}
      >
        <div
          style={{
            height: rowVirtualizer.getTotalSize(),
            position: 'relative',
            width: '100%',
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualItem) => (
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                transform: `translateY(${virtualItem.start}px)`,
                width: '100%',
              }}
              key={virtualItem.key}
              data-index={virtualItem.index}
              ref={rowVirtualizer.measureElement}
            >
              {children[virtualItem.index]}
            </div>
          ))}
        </div>
      </div>
    ) : (
      <div>{children}</div>
    );
  };
}

const CustomOption = ({ children, ...props }: OptionProps) => {
  const { onMouseMove, onMouseOver, ...rest } = props.innerProps;
  const newProps = { ...props, innerProps: rest };
  return (
    <components.Option className="auto-complete-custom-option" {...newProps}>
      {children}
    </components.Option>
  );
};

function isGroupedAutoCompleteOptions<ValueType = number>(
  options: AutoCompleteOption<ValueType>[],
): options is GroupedAutoCompleteOption<ValueType>[] {
  return (
    options.length > 0 &&
    (options[0] as GroupedAutoCompleteOption<ValueType>).options !== undefined
  );
}

function AutoCompleteSelect<ValueType = number>({
  onChange,
  value,
  options,
  isDisabled,
  isLoading,
  isClearable = true,
  placeholder,
  maxHeight,
  ...restProps
}: AutoCompleteSelectProps<ValueType>) {
  const theme = useTheme();

  const filterConfig = {
    ignoreCase: true,
    ignoreAccents: true,
    trim: true,
    matchFrom: 'any' as const,
  };

  const [flatOptions, isGrouped] = useMemo(() => {
    if (isGroupedAutoCompleteOptions(options)) {
      return [options.flatMap((group) => group.options), true];
    } else {
      return [options as SingleAutoCompleteOption<ValueType>[], false];
    }
  }, [options]);

  const selectValue =
    flatOptions.find((option) => option.value === value) ?? null;

  return (
    <Select
      isDisabled={isDisabled}
      isLoading={isLoading}
      openMenuOnClick={true}
      components={{
        DropdownIndicator: null,
        Option: CustomOption,
        MenuList: createCustomMenuList(isGrouped, maxHeight),
      }}
      value={selectValue}
      filterOption={createFilter(filterConfig)}
      options={options}
      isClearable={isClearable}
      menuPortalTarget={document.body}
      menuPlacement="auto"
      placeholder={placeholder}
      styles={{
        clearIndicator: (provided) => ({
          ...provided,
          padding: '0 4px',
        }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        container: (provided) => ({
          ...provided,
          width: '100%',
        }),
        control: (provided) => ({
          ...provided,
          minHeight: '32px',
          borderRadius: 0,
          borderColor: theme.colors.input.border,
        }),
        singleValue: (provided) => ({
          ...provided,
          color: '#0D1721',
        }),
        valueContainer: (provided) => ({
          ...provided,
          height: '30px',
          alignContent: 'center',
          fontSize: 'small',
        }),
        option: (provided) => ({
          ...provided,
          fontSize: 'small',
        }),
      }}
      onChange={(
        newValue: SingleValue<SingleAutoCompleteOption<ValueType>> | null,
      ) => onChange && onChange(newValue?.value ?? null)}
      {...restProps}
    />
  );
}

export default AutoCompleteSelect;
